import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { PlusIcon } from "app/icons";
import React from "react";
import { useDispatch } from "react-redux";

import { customersActions } from "../../../redux/slice";
import { CustomerContactPerson } from "../../../types";
import { useCustomerDetailsForm } from "../hooks/useCustomerDetailsForm";
import CardActionsMenu from "./CardActionsMenu";
import AddContactModal from "./customerDetailsModal";

const CustomerDetailsForm = () => {
  const {
    t,
    styles,
    formData,
    statusValue,
    statusLabel,
    getStatusBadgeClassName,
    onStatusChange,
    statusOptions,
    contacts,
  } = useCustomerDetailsForm();

  const dispatch = useDispatch();

  const handleDeleteContact = (contactId: number) => {
    dispatch(customersActions.deleteCustomerContact(contactId));
  };

  const contactCardHeader = (title: string, contactId: number) => (
    <Box className={styles.cardHeader}>
      <CardActionsMenu onDelete={() => handleDeleteContact(contactId)} />
      <Typography className={styles.cardTitle}>{title}</Typography>
    </Box>
  );

  const labeledField = (labelKey: string, control: React.ReactNode) => (
    <Box className={styles.field}>
      <Typography className={styles.label}>{t(labelKey)}</Typography>
      {control}
    </Box>
  );

  const textField = (
    labelKey: string,
    value: string,
    className = styles.input,
    dir?: "ltr"
  ) =>
    labeledField(
      labelKey,
      <TextField
        value={value}
        fullWidth
        className={className}
        inputProps={dir ? { dir } : {}}
      />
    );

  const selectChevron = (content: React.ReactNode) => (
    <Box className={styles.selectValue}>
      {content}
      <KeyboardArrowDownIcon className={styles.selectChevron} />
    </Box>
  );

  const statusBadge = (status: string) => (
    <span className={getStatusBadgeClassName(status)}>
      {statusLabel(status)}
    </span>
  );

  const roleSelect = (role: string) =>
    labeledField(
      "CUSTOMERS.DETAILS.FIELDS.ROLE",
      <FormControl fullWidth className={styles.formControl}>
        <Select
          value={role}
          displayEmpty
          className={styles.select}
          IconComponent={() => null}
          renderValue={(value) =>
            selectChevron(<span>{(value as string) || ""}</span>)
          }
        >
          <MenuItem value={role}>{role || ""}</MenuItem>
        </Select>
      </FormControl>
    );

  const renderContactCard = (
    contact: CustomerContactPerson,
    index: number
  ) => (
    <Box key={contact.contactId || index} className={styles.contactCard}>
      {contactCardHeader(
        t("CUSTOMERS.DETAILS.CONTACT_PERSON_LABEL", { index: index + 1 }),
        contact.contactId
      )}
      <Box className={styles.contactFieldsRow}>
        {textField(
          "CUSTOMERS.DETAILS.FIELDS.CONTACT_PERSON",
          contact.contactName || ""
        )}
        {roleSelect(contact.role || "")}
        {textField(
          "CUSTOMERS.DETAILS.FIELDS.CONTACT_PHONE",
          contact.phoneNo || "",
          styles.phoneInput,
          "ltr"
        )}
        {textField(
          "CUSTOMERS.DETAILS.FIELDS.CONTACT_EMAIL",
          contact.email || ""
        )}
      </Box>
    </Box>
  );

  return (
    <Box className={styles.root}>
      <Box className={styles.section}>
        <Typography className={styles.sectionTitle}>
          {t("CUSTOMERS.DETAILS.TITLE")}
        </Typography>
        <Box className={styles.fieldsRow}>
          {textField(
            "CUSTOMERS.DETAILS.FIELDS.CUSTOMER_NAME",
            formData.customerName || ""
          )}
          {textField(
            "CUSTOMERS.DETAILS.FIELDS.ADDRESS",
            formData.address || ""
          )}
          {labeledField(
            "CUSTOMERS.DETAILS.FIELDS.STATUS",
            <FormControl fullWidth className={styles.formControl}>
              <Select
                value={statusValue}
                onChange={(event) => onStatusChange(event.target.value)}
                className={styles.select}
                IconComponent={() => null}
                renderValue={(value) =>
                  selectChevron(statusBadge(value as string))
                }
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {statusLabel(option)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      <Box className={styles.contactsSection}>
        <Box className={styles.contactsHeader}>
          <Button
            variant="contained"
            className={styles.addButton}
            startIcon={<PlusIcon />}
            onClick={() => dispatch(customersActions.ContactModalOpen())}
          >
            {t("CUSTOMERS.DETAILS.ACTIONS.ADD_CONTACT")}
          </Button>
          <Typography className={styles.contactsTitle}>
            {t("CUSTOMERS.DETAILS.SECTIONS.CONTACT_PERSONS")}
          </Typography>
        </Box>
        <Box className={styles.contactsList}>
          {contacts.map((contact, index) => renderContactCard(contact, index))}
        </Box>
      </Box>

      <AddContactModal />
    </Box>
  );
};

export default CustomerDetailsForm;
